import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Button, Modal, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useAuth } from "../services/authProvider";

const MANAGE_POSITIONS = [3, 4, 8, 9, 99];

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" }) : "-";

export default function Announcements() {
  const { setLoading } = useLoading();
  const { posIds } = useAuth();
  const canManage = (posIds || []).some((p) => MANAGE_POSITIONS.includes(p));

  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");
  useEffect(() => {}, [skin]);

  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ann_title: "", ann_content: "", ann_pinned: false });

  const loadItems = () => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getAnnouncements`)
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", "ไม่สามารถโหลดประกาศได้", "error"); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadItems(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ann_title: "", ann_content: "", ann_pinned: false });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ann_title: item.ann_title, ann_content: item.ann_content || "", ann_pinned: !!item.ann_pinned });
    setShowModal(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.ann_title.trim()) return Swal.fire("แจ้งเตือน", "กรุณาระบุหัวข้อประกาศ", "warning");

    const payload = { ann_title: form.ann_title.trim(), ann_content: form.ann_content, ann_pinned: form.ann_pinned ? 1 : 0 };
    const req = editing
      ? axiosInstance.put(`${process.env.REACT_APP_API}/updateAnnouncement/${editing.ann_id}`, payload)
      : axiosInstance.post(`${process.env.REACT_APP_API}/addAnnouncement`, payload);

    setLoading(true);
    req
      .then(() => { setShowModal(false); Swal.fire("สำเร็จ", editing ? "แก้ไขประกาศเรียบร้อย" : "เพิ่มประกาศเรียบร้อย", "success"); loadItems(); })
      .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "บันทึกไม่สำเร็จ", "error"); })
      .finally(() => setLoading(false));
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "ยืนยันการลบ", text: `ลบประกาศ "${item.ann_title}" ?`, icon: "warning",
      showCancelButton: true, confirmButtonText: "ลบ", cancelButtonText: "ยกเลิก", confirmButtonColor: "#dc3545",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.delete(`${process.env.REACT_APP_API}/deleteAnnouncement/${item.ann_id}`)
        .then(() => { Swal.fire("ลบแล้ว", "ลบประกาศเรียบร้อย", "success"); loadItems(); })
        .catch((err) => { console.error(err); Swal.fire("ผิดพลาด", err.response?.data?.message || "ลบไม่สำเร็จ", "error"); })
        .finally(() => setLoading(false));
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ทั่วไป</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ประกาศ/ข่าวสาร</li>
            </ol>
            <h4 className="main-title mb-0">ประกาศ/ข่าวประชาสัมพันธ์</h4>
          </div>
          {canManage && (
            <Button variant="primary" className="d-flex align-items-center gap-2" onClick={openAdd}>
              <i className="ri-add-line fs-18 lh-1"></i> เพิ่มประกาศ
            </Button>
          )}
        </div>

        <Row className="g-3">
          {items.length === 0 ? (
            <Col xl="12"><Card className="card-one"><Card.Body className="text-center text-secondary py-5">ยังไม่มีประกาศ</Card.Body></Card></Col>
          ) : (
            items.map((item) => (
              <Col xl="12" key={item.ann_id}>
                <Card className="card-one">
                  <Card.Body>
                    <div className="d-flex align-items-start justify-content-between">
                      <div>
                        <h6 className="mb-1">
                          {item.ann_pinned ? <Badge bg="warning" text="dark" className="me-2"><i className="ri-pushpin-line"></i> ปักหมุด</Badge> : null}
                          {item.ann_title}
                        </h6>
                        <small className="text-secondary">
                          {formatDateTime(item.ann_created_at)}
                          {item.created_by_name ? ` • โดย ${item.created_by_name}` : ""}
                        </small>
                      </div>
                      {canManage && (
                        <div className="d-flex gap-1">
                          <Button size="sm" variant="outline-primary" onClick={() => openEdit(item)}><i className="ri-edit-line"></i></Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item)}><i className="ri-delete-bin-line"></i></Button>
                        </div>
                      )}
                    </div>
                    {item.ann_content && <p className="mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>{item.ann_content}</p>}
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>

        <Footer />
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title as="h6">{editing ? "แก้ไขประกาศ" : "เพิ่มประกาศ"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>หัวข้อ <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={form.ann_title} onChange={(e) => setForm({ ...form, ann_title: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>เนื้อหา</Form.Label>
              <Form.Control as="textarea" rows={5} value={form.ann_content} onChange={(e) => setForm({ ...form, ann_content: e.target.value })} />
            </Form.Group>
            <Form.Check
              type="switch" id="ann-pinned" label="ปักหมุดไว้บนสุด"
              checked={form.ann_pinned} onChange={(e) => setForm({ ...form, ann_pinned: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" type="submit">บันทึก</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </React.Fragment>
  );
}
